import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import {
  FiTarget, FiPlus, FiSearch, FiEdit2, FiTrash2, FiPlay, FiPause, FiEye, FiSend, FiClock, FiCheckCircle, FiXCircle
} from 'react-icons/fi';

/* ══ CONQUER Cyan Theme ══ */
const THEME = {
  primary: '#00B4D8',
  secondary: '#48CAE4',
  accent: '#90E0EF',
  success: '#059669',
  warning: '#D97706',
  danger: '#DC2626',
  dark: '#0A1628',
  slate: '#0096C7',
  muted: '#48CAE4',
};

const StatusBadge = ({ status }) => {
  const configs = {
    pending: { bg: `${THEME.warning}15`, color: THEME.warning, icon: FiClock, label: 'PENDING' },
    active: { bg: `${THEME.success}15`, color: THEME.success, icon: FiCheckCircle, label: 'ACTIVE' },
    paused: { bg: `${THEME.slate}15`, color: THEME.slate, icon: FiPause, label: 'PAUSED' },
    completed: { bg: `${THEME.primary}15`, color: THEME.primary, icon: FiCheckCircle, label: 'COMPLETED' },
    failed: { bg: `${THEME.danger}15`, color: THEME.danger, icon: FiXCircle, label: 'FAILED' },
  };
  const cfg = configs[status] || configs.pending;
  const Icon = cfg.icon;
  return (
    <span style={{ padding: '5px 12px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: cfg.bg, color: cfg.color, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <Icon size={12} /> {cfg.label}
    </span>
  );
};

export default function CampaignManagement() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', type: 'sms', status: 'active', priority: 'medium', dataLimit: 0, allowReuse: false, failCount: 5 });
  const [availableData, setAvailableData] = useState(0);

  const fetchCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/campaigns?page=${page}&search=${search}&status=${statusFilter}`);
      setCampaigns(data.campaigns || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch { toast.error('Failed to fetch campaigns'); }
    finally { setLoading(false); }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  // Fetch available unassigned data count
  const fetchAvailableData = async () => {
    try {
      const { data } = await api.get('/admin/campaigns/available-data');
      setAvailableData(data.availableData || 0);
    } catch (e) { console.error('Failed to fetch available data'); }
  };

  useEffect(() => { fetchAvailableData(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', type: 'sms', status: 'active', priority: 'medium', dataLimit: 0, allowReuse: false, failCount: 5 });
    fetchAvailableData();
    setShowModal(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, type: c.type || 'sms', status: c.status || 'active', priority: c.priority || 'medium', dataLimit: 0, allowReuse: false, failCount: c.failCount || 5 });
    fetchAvailableData();
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/campaigns/${editing._id}`, form);
        toast.success('Campaign updated');
      } else {
        await api.post('/admin/campaigns', form);
        toast.success('Campaign created');
      }
      setShowModal(false);
      fetchCampaigns();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this campaign?')) return;
    try { await api.delete(`/admin/campaigns/${id}`); toast.success('Campaign deleted'); fetchCampaigns(); }
    catch { toast.error('Delete failed'); }
  };

  const handleToggleStatus = async (id, current) => {
    const newStatus = current === 'active' ? 'paused' : 'active';
    try { await api.patch(`/admin/campaigns/${id}/status`, { status: newStatus }); toast.success('Status updated'); fetchCampaigns(); }
    catch { toast.error('Update failed'); }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px', borderRadius: 10, border: '2px solid #E0E7FF',
    fontSize: 14, color: THEME.dark, background: '#FAFAFF', outline: 'none',
  };

  // Summary statistics
  const stats = {
    total: campaigns.length,
    active: campaigns.filter(c => c.status === 'active').length,
    pending: campaigns.filter(c => c.status === 'pending').length,
    completed: campaigns.filter(c => c.status === 'completed').length,
  };

  return (
    <div style={{ padding: '0 4px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, color: THEME.dark, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <FiTarget size={28} style={{ color: THEME.primary }} />
            Outreach Command
          </h1>
          <p style={{ fontSize: 14, color: THEME.muted, marginTop: 6 }}>{total} campaigns configured</p>
        </div>
        <button
          onClick={openCreate}
          style={{
            padding: '12px 24px', borderRadius: 12,
            background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.secondary})`,
            color: '#fff', fontSize: 14, fontWeight: 700, border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 8,
            boxShadow: `0 6px 20px ${THEME.primary}30`,
          }}
        >
          <FiPlus size={18} /> New Campaign
        </button>
      </div>

      {/* Mini Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Total', value: stats.total, color: THEME.slate, icon: FiTarget },
          { label: 'Active', value: stats.active, color: THEME.success, icon: FiPlay },
          { label: 'Pending', value: stats.pending, color: THEME.warning, icon: FiClock },
          { label: 'Completed', value: stats.completed, color: THEME.primary, icon: FiCheckCircle },
        ].map((s, i) => (
          <div key={i} style={{
            background: '#fff', borderRadius: 14, padding: 18,
            border: `1px solid ${THEME.accent}30`, boxShadow: '0 2px 12px rgba(124,58,237,0.06)',
            display: 'flex', alignItems: 'center', gap: 14,
          }}>
            <div style={{ width: 44, height: 44, borderRadius: 12, background: `${s.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <s.icon size={22} style={{ color: s.color }} />
            </div>
            <div>
              <div style={{ fontSize: 22, fontWeight: 800, color: THEME.dark }}>{s.value}</div>
              <div style={{ fontSize: 12, color: THEME.muted, fontWeight: 600 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{
        background: '#fff', borderRadius: 14, padding: 16, marginBottom: 24,
        border: `1px solid ${THEME.accent}30`, boxShadow: '0 2px 12px rgba(124,58,237,0.06)',
        display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 200 }}>
          <FiSearch size={20} style={{ color: THEME.muted }} />
          <input
            type="text" placeholder="Search campaigns..."
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            style={{ ...inputStyle, border: 'none', background: 'transparent', padding: 0 }}
          />
        </div>
        <select
          value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          style={{ ...inputStyle, width: 'auto', minWidth: 140, cursor: 'pointer' }}
        >
          <option value="">All Status</option>
          <option value="pending">Pending</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="completed">Completed</option>
        </select>
      </div>

      {/* Table */}
      <div style={{
        background: '#fff', borderRadius: 18, overflow: 'hidden',
        border: `1px solid ${THEME.accent}30`, boxShadow: '0 4px 24px rgba(124,58,237,0.08)',
      }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <div style={{ width: 48, height: 48, border: '4px solid #E0E7FF', borderTopColor: THEME.primary, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto' }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${THEME.primary}08, ${THEME.accent}05)` }}>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase', letterSpacing: 0.5 }}>Campaign</th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Type</th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Status</th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Total</th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Sent</th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Failed</th>
                  <th style={{ padding: '16px 20px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Created</th>
                  <th style={{ padding: '16px 20px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: THEME.dark, textTransform: 'uppercase' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c, i) => (
                  <tr key={c._id} style={{ borderBottom: '1px solid #F0EEFF', background: i % 2 === 0 ? '#fff' : '#FAFAFF' }}>
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{
                          width: 40, height: 40, borderRadius: 10,
                          background: `linear-gradient(135deg, ${THEME.secondary}, ${THEME.accent})`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <FiSend size={18} style={{ color: '#fff' }} />
                        </div>
                        <div>
                          <div style={{ fontWeight: 700, color: THEME.dark, fontSize: 14 }}>{c.name}</div>
                          <div style={{ fontSize: 11, color: THEME.muted }}>ID: {c._id?.slice(-6)}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: THEME.slate, textTransform: 'capitalize' }}>{c.type?.replace('_', ' ') || 'SMS'}</span>
                    </td>
                    <td style={{ padding: '14px 20px' }}><StatusBadge status={c.status} /></td>
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: THEME.dark }}>{(c.totalAssigned || 0).toLocaleString()}</div>
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: THEME.success }}>{(c.totalSent || 0).toLocaleString()}</div>
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: THEME.danger }}>{(c.totalFailed || 0).toLocaleString()}</div>
                    </td>
                    <td style={{ padding: '14px 20px', color: THEME.muted, fontSize: 13 }}>{new Date(c.createdAt).toLocaleDateString()}</td>
                    <td style={{ padding: '14px 20px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button onClick={() => navigate(`/admin/campaigns/${c._id}`)} title="View" style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #E0E7FF', background: '#fff', cursor: 'pointer', color: THEME.slate }}><FiEye size={14} /></button>
                        <button onClick={() => openEdit(c)} title="Edit" style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #E0E7FF', background: '#fff', cursor: 'pointer', color: THEME.slate }}><FiEdit2 size={14} /></button>
                        <button onClick={() => handleToggleStatus(c._id, c.status)} title={c.status === 'active' ? 'Pause' : 'Activate'}
                          style={{ padding: '8px 10px', borderRadius: 8, border: 'none', background: c.status === 'active' ? `${THEME.warning}15` : `${THEME.success}15`, cursor: 'pointer', color: c.status === 'active' ? THEME.warning : THEME.success }}>
                          {c.status === 'active' ? <FiPause size={14} /> : <FiPlay size={14} />}
                        </button>
                        <button onClick={() => handleDelete(c._id)} title="Delete" style={{ padding: '8px 10px', borderRadius: 8, border: 'none', background: `${THEME.danger}15`, cursor: 'pointer', color: THEME.danger }}><FiTrash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {campaigns.length === 0 && (
                  <tr><td colSpan="8" style={{ padding: 60, textAlign: 'center', color: THEME.muted }}>No campaigns found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: 6, padding: 20, borderTop: '1px solid #F0EEFF' }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #E0E7FF', background: '#fff', cursor: page === 1 ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, color: THEME.slate }}>‹</button>
            {[...Array(Math.min(pages, 5))].map((_, i) => (
              <button key={i} onClick={() => setPage(i + 1)}
                style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: page === i + 1 ? THEME.primary : '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: page === i + 1 ? '#fff' : THEME.slate }}>{i + 1}</button>
            ))}
            <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}
              style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #E0E7FF', background: '#fff', cursor: page === pages ? 'not-allowed' : 'pointer', fontSize: 13, fontWeight: 600, color: THEME.slate }}>›</button>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(30,27,75,0.5)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowModal(false)}>
          <div style={{ background: '#fff', borderRadius: 20, width: '100%', maxWidth: 480, boxShadow: '0 24px 60px rgba(124,58,237,0.2)' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '24px 28px', borderBottom: '1px solid #F0EEFF', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: THEME.dark, margin: 0 }}>{editing ? 'Edit Campaign' : 'New Campaign'}</h2>
              <button onClick={() => setShowModal(false)} style={{ width: 36, height: 36, borderRadius: 10, border: 'none', background: '#F0EEFF', cursor: 'pointer', fontSize: 20, color: THEME.muted }}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ padding: 28, display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>Campaign Name</label>
                  <input style={inputStyle} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="Enter campaign name" />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>Type</label>
                    <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                      <option value="sms">SMS</option>
                      <option value="whatsapp">WhatsApp</option>
                      <option value="whatsapp_image">WhatsApp Image</option>
                      <option value="whatsapp_video">WhatsApp Video</option>
                      <option value="whatsapp_document">WhatsApp Document</option>
                      <option value="rcs">RCS</option>
                      <option value="email">Email</option>
                      <option value="newsletter">Newsletter</option>
                      <option value="push_notification">Push Notification</option>
                      <option value="voice_call">Voice Call</option>
                      <option value="status_messaging">Status Messaging</option>
                      <option value="custom">Custom</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>Priority</label>
                    <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>Status</label>
                  <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                    <option value="active">Active</option>
                    <option value="paused">Paused</option>
                    <option value="stopped">Stopped</option>
                    <option value="completed">Completed</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>Fail Count (retry threshold)</label>
                  <input type="number" min="1" max="2000" style={inputStyle} value={form.failCount} onChange={e => setForm({ ...form, failCount: parseInt(e.target.value) || 5 })} placeholder="Retries before fail" />
                </div>
                </div>

                {/* Data Assignment Section - only show for new campaigns */}
                {!editing && (
                <div style={{ background: `${THEME.primary}08`, borderRadius: 12, padding: 16, marginTop: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: THEME.dark, marginBottom: 12 }}>Data Assignment</div>
                  <div style={{ fontSize: 12, color: THEME.muted, marginBottom: 10 }}>Available: <span style={{ fontWeight: 700, color: THEME.success }}>{availableData.toLocaleString()}</span> records unassigned</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div>
                      <label style={{ fontSize: 12, fontWeight: 600, color: THEME.muted, display: 'block', marginBottom: 6 }}>Assign Data Count</label>
                      <input type="number" min="0" max={availableData || 999999999} style={inputStyle} value={form.dataLimit} onChange={e => setForm({ ...form, dataLimit: parseInt(e.target.value) || 0 })} placeholder="0 = no auto-assign" />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 24 }}>
                      <input type="checkbox" id="allowReuse" checked={form.allowReuse} onChange={e => setForm({ ...form, allowReuse: e.target.checked })} style={{ width: 18, height: 18, cursor: 'pointer', accentColor: THEME.primary }} />
                      <label htmlFor="allowReuse" style={{ fontSize: 12, color: THEME.dark, cursor: 'pointer' }}>Allow Reuse (pull from other campaigns)</label>
                    </div>
                  </div>
                </div>
                )}
              </div>
              <div style={{ padding: '20px 28px', borderTop: '1px solid #F0EEFF', display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowModal(false)} style={{ padding: '12px 24px', borderRadius: 10, border: '1px solid #E0E7FF', background: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: THEME.slate }}>Cancel</button>
                <button type="submit" style={{ padding: '12px 24px', borderRadius: 10, border: 'none', background: `linear-gradient(135deg, ${THEME.primary}, ${THEME.secondary})`, cursor: 'pointer', fontSize: 14, fontWeight: 700, color: '#fff', boxShadow: `0 4px 12px ${THEME.primary}30` }}>{editing ? 'Update' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
